import { ref } from 'vue'

/**
 * Light or dark, chosen by the person or left to the system. The choice
 * is kept in this browser; index.html applies it before the first paint,
 * so a dark page never flashes white.
 */
export type ThemeChoice = 'light' | 'dark' | 'system'

const KEY = 'ls-theme'
const media =
  typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: dark)') : null

function read(): ThemeChoice {
  try {
    const v = localStorage.getItem(KEY)
    return v === 'light' || v === 'dark' ? v : 'system'
  } catch {
    return 'system'
  }
}

const choice = ref<ThemeChoice>(read())
/** What is showing now, the system's answer included. */
const dark = ref(false)

function apply(): void {
  dark.value = choice.value === 'dark' || (choice.value === 'system' && media?.matches === true)
  document.documentElement.classList.toggle('dark', dark.value)
}

media?.addEventListener('change', () => {
  if (choice.value === 'system') apply()
})
if (typeof document !== 'undefined') apply()

export function useTheme() {
  function set(next: ThemeChoice): void {
    choice.value = next
    try {
      if (next === 'system') localStorage.removeItem(KEY)
      else localStorage.setItem(KEY, next)
    } catch {
      // A private window: the choice lasts for this page.
    }
    apply()
  }
  return { choice, dark, set, toggle: () => set(dark.value ? 'light' : 'dark') }
}
